import { useMemo } from "react";
import { Lock } from "lucide-react";
import { formatDateTime } from "@/lib/formatters";

interface ReservationWindowProps {
  /** ISO instant the reservation lapses, exactly as the session returned
   *  it. Never recomputed from the batch size on this side: the window is
   *  `count x minutes-per-record`, floored and capped, and all three of
   *  those numbers are per-project settings the frontend doesn't hold. */
  expiresAt: string;
}

/** Round to whole hours above an hour, to minutes below it. Read once,
 *  at the top of a session, so "3h" is the window that was granted rather
 *  than a countdown — SessionExpiryBanner owns the endgame. */
function humanizeWindow(ms: number): string | null {
  const minutes = Math.round(ms / 60_000);
  if (minutes <= 0) return null;
  if (minutes < 60) return `${minutes}min`;
  return `${Math.round(minutes / 60)}h`;
}

/** How long the batch is held, and until when.
 *
 *  The hold follows the size of the lot, so this varies: a lot of 20 says
 *  3h, a lot of 500 says 24h. Both halves come from `expires_at` — the
 *  duration by subtracting now from it, the timestamp verbatim — because
 *  the sizing rule and its floor and ceiling are project configuration. */
export function ReservationWindow({ expiresAt }: ReservationWindowProps) {
  const expiry = new Date(expiresAt).getTime();

  // Measured once per session: the operator wants to know what they were
  // given, not to watch it tick down.
  const window = useMemo(
    () => (Number.isFinite(expiry) ? humanizeWindow(expiry - Date.now()) : null),
    [expiry],
  );

  if (!Number.isFinite(expiry)) return null;

  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-slate-400"
      title={`This batch is reserved to you until ${formatDateTime(expiresAt)}`}
    >
      <Lock className="h-3 w-3" />
      {window ? (
        <>
          Reserved for {window}
          <span className="text-slate-300">·</span>
        </>
      ) : null}
      until {formatDateTime(expiresAt)}
    </span>
  );
}
